import type { CmpAdapter, CmpDeps } from './types'

// SourcePoint CMP adapter — a fallback for Didomi, selected when feature_flags.gdpr_consent_mode is 'sourcepoint'. It
// mirrors the original (pre-Didomi) implementation but plugs into the shared facade via CmpDeps. Delete
// this file (and the sourcepoint branches in _gdpr_consent.html.erb and boot/gdpr.ts, plus the secret)
// once the Didomi rollout is confirmed.

type VendorConsentData = {
  consentedVendors: Array<{ name: string; _id: string }>
  grants: Record<string, { vendorGrant: boolean }>
  disabled?: boolean
}

declare function __sf_tcfapi(
  command: 'getCustomVendorConsents',
  version: number,
  callback: (data: VendorConsentData, success: boolean) => void,
): void

declare global {
  interface SourcePointInstance {
    executeMessaging(): void
    config: {
      events: {
        onConsentReady?: () => void | Promise<void>
        onMessageReady?: () => void
        onError?: (...args: unknown[]) => void
      }
    }
    gdpr: { loadPrivacyManagerModal(id: string): void }
  }

  interface Window {
    _sp_?: SourcePointInstance
    _sp_onLoad?: (callback: () => void) => void
    _sp_ready?: boolean
    _sp_available?: boolean
  }
}

export function createSourcepointAdapter(deps: CmpDeps): CmpAdapter {
  let vendorConsent: VendorConsentData | null = null

  function updateVendorGrant(): void {
    __sf_tcfapi('getCustomVendorConsents', 2, (data, success) => {
      if (success) {
        vendorConsent = data
      } else if (!data.disabled) {
        console.error('[SourcePoint] Invalid response from TCF API')
      }
      deps.signalConsentChanged()
    })
  }

  return {
    init() {
      window._sp_onLoad!(() => {
        // _sp_, _sp_onLoad etc. are defined in _gdpr_consent.html.erb (sourcepoint branch).
        window._sp_!.config.events = {
          async onConsentReady() {
            updateVendorGrant()
          },
          onMessageReady() {
            deps.markBannerShown()
          },
          onError(...args: unknown[]) {
            console.error('[SourcePoint] Unknown error in GDPR consent', args)
            updateVendorGrant()
          },
        }

        deps.whenMessagingAllowed(() => {
          try {
            window._sp_!.executeMessaging()
          } catch {
            console.error('[SourcePoint] Could not execute messaging')
            updateVendorGrant()
          }
        })
      })
    },

    // SourcePoint keys vendors by name (not by id), so unlike Didomi there is no name->id map.
    hasVendorGrant(vendorName: string): boolean {
      if (!vendorConsent) {
        return false
      }

      const { consentedVendors, grants } = vendorConsent
      const vendorInfo = consentedVendors.find(({ name }) => name === vendorName)

      return !!(vendorInfo && grants[vendorInfo._id]?.vendorGrant)
    },

    openGdprManager(privacyManagerId: string): void {
      window._sp_?.gdpr.loadPrivacyManagerModal(privacyManagerId)
    },
  }
}
